import useLocale from "@lib/hooks/useLocale";
import usePartnerContext from "@lib/hooks/usePartnerContext";
import {
  getBudgetCheckPath,
  getPartnerBudgetCheckPath,
  getPartnerSearchProfileBudgetCheckPath,
} from "@lib/routing";
import ApplicationStepHeader from "@modules/application/components/ApplicationStepHeader";
import BudgetCheckDynamicForm from "@modules/application/components/BudgetCheckDynamicForm";
import { FormState } from "@modules/application/context/ApplicationContext";
import useApplicationContext from "@modules/application/hooks/useApplicationContext";
import useSteps from "@modules/application/hooks/useSteps";
import getBudgetCheckSteps from "@modules/application/lib/budgetCheck";
import {
  BudgetCheckFormValues,
  BudgetCheckStepId,
} from "@modules/application/lib/budgetCheck/types";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { FC, useEffect, useMemo, useRef } from "react";
import ApplicationSteps from "../../components/ApplicationSteps";
import SplitLayout from "../../components/SplitLayout";

interface BudgetCheckProps {
  stepId: string;
  withSearchProfile?: boolean;
}
const BudgetCheck: FC<BudgetCheckProps> = ({
  stepId,
  withSearchProfile = false,
}) => {
  const { t } = useTranslation("budget-check");
  const { partner } = usePartnerContext();
  const { state, dispatch } = useApplicationContext();
  const locale = useLocale();
  const ref = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const steps = useMemo(
    () =>
      getBudgetCheckSteps(
        t,
        locale,
        false,
        !!state.lead?.id,
        partner?.slug,
        withSearchProfile,
      ),
    [t, locale, partner, state.lead, withSearchProfile],
  );

  const formSteps = useSteps<BudgetCheckFormValues>({
    stepId,
    steps: steps,
    formState: state.budgetCheckFormState,
    hasZeroStep: false,
    onInvalidState() {
      if (steps[0]) {
        router.query.stepId = steps[0].id;
        router.replace(router);
      }
    },
    onSetForm(form: FormState<BudgetCheckFormValues>) {
      dispatch({ type: "SET_BUDGET_CHECK_FORM_STATE", form });
    },
    onStepChange(step) {
      let routerAction = router.push;
      if (step.id === BudgetCheckStepId.result) {
        routerAction = router.replace;
      }

      if (partner?.id) {
        if (withSearchProfile) {
          routerAction(
            getPartnerSearchProfileBudgetCheckPath(locale, partner.id, step.id),
          );
        } else {
          routerAction(getPartnerBudgetCheckPath(locale, partner.id, step.id));
        }
      } else {
        routerAction(getBudgetCheckPath(locale, step.id));
      }
    },
    onGoBack(stepId?: string) {
      if (stepId) {
        if (partner?.id) {
          router.push(getPartnerBudgetCheckPath(locale, partner.id, stepId));
        } else {
          router.push(getBudgetCheckPath(locale, stepId));
        }
      } else {
        router.back();
      }
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [formSteps.currentStepNumber]);

  const Component = formSteps.currentStep.component || BudgetCheckDynamicForm;

  return (
    <SplitLayout>
      <SplitLayout.Aside>
        <ApplicationSteps
          onPrevious={formSteps.handlePreviousStep}
          steps={formSteps.nextSteps}
          state={formSteps.currentStepNumber + 1}
          step={formSteps.currentStep}
          value={formSteps.value}
        />
      </SplitLayout.Aside>
      <SplitLayout.Body
        currentStepNumber={formSteps.currentStepNumber + 1}
        numberOfSteps={formSteps.numberOfSteps}
        state={formSteps.currentStepNumber}
      >
        <ApplicationStepHeader
          config={formSteps.currentStep.config}
          currentStepNumber={formSteps.currentStepNumber + 1}
          value={formSteps.value}
        />
        <div ref={ref} className="scroll-m-[148px] m:scroll-m-5xl">
          <Component
            steps={formSteps.nextSteps}
            onPrevious={formSteps.handlePreviousStep}
            onNext={formSteps.handleNextStep}
            numberOfSteps={formSteps.numberOfSteps}
            hasZeroStep={false}
            currentStepNumber={formSteps.currentStepNumber}
            step={formSteps.currentStep}
            value={formSteps.value}
          />
        </div>
      </SplitLayout.Body>
    </SplitLayout>
  );
};

export default BudgetCheck;
